import React, { useState, useRef, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import '../styles/SignalsFirmware.css';

function SignalsFirmware() {
  const [device, setDevice] = useState('');
  const [model, setModel] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [hwVersion, setHWVersion] = useState('');
  const [swVersion, setSWVersion] = useState('');
  const [disInfo, setDISInfo] = useState(false);
  const [isConnected, setConnection] = useState(false);
  const [isFileLoaded, setLoadFile] = useState(false);
  const [showBar, setProgressBar] = useState(false);
  const [otaFile, setOtaFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0.0);
  const [barStatus, setBarStatus] = useState('success');
  const [showStatus, setShowStatus] = useState(false);
  const [status, setStatus] = useState('');
  const [logMessages, setLogMessages] = useState([]);
  const [signalName, setSignalName] = useState('');
  const [signalId, setSignalId] = useState('');
  const [signalChannel, setSignalChannel] = useState('');
  const [secondaryChannel, setSecondaryChannel] = useState('');
  const [hasSecondaryChannelSetting, setHasSecondaryChannelSetting] = useState(false);
  const [frequencyHopping, setFrequencyHopping] = useState(0);
  const [hasFrequencyHoppingSetting, setHasFrequencyHoppingSetting] = useState(false);
  const [inputDebug, setInputDebug] = useState(0);
  const [blockAvailableSetting, setBlockAvailableSetting] = useState(0);
  const [firmwareFiles, setFirmwareFiles] = useState([]);
  const [selectedFirmware, setSelectedFirmware] = useState('');

  const clientDeviceRef = useRef(null);
  const fwStatusRef = useRef(0);
  const cmdStatusRef = useRef(0);
  const expectedIndexRef = useRef(0);

  const crc16 = (init, data, len) => {
    let crc = init;
    for (let i = 0; i < len; i++) {
      crc ^= data[i] << 8;
      for (let j = 0; j < 8; j++) {
        if (crc & 0x8000) {
          crc = ((crc << 1) ^ 0x1021) & 0xffff;
        } else {
          crc <<= 1;
        }
      }
    }
    return crc & 0xffff;
  };

  const addLog = (msg) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogMessages(prev => [...prev, `[${timestamp}] ${msg}`]);
  };

  useEffect(() => {
    // Load available firmware files from public/firmware directory
    const loadFirmwareList = async () => {
      try {
        const response = await fetch('/firmware/manifest.json');
        if (response.ok) {
          const data = await response.json();
          setFirmwareFiles(data.files || []);
        }
      } catch (error) {
        console.log('Could not load firmware manifest');
      }
    };
    loadFirmwareList();
  }, []);

  const loadSelectedFirmware = async (filename) => {
    try {
      const response = await fetch(`/firmware/${filename}`);
      if (response.ok) {
        const buffer = await response.arrayBuffer();
        setOtaFile(buffer);
        setLoadFile(true);
        addLog(`Firmware loaded: ${filename}`);
      } else {
        setStatus(`Error loading firmware: ${filename}`);
        setBarStatus('danger');
        setShowStatus(true);
      }
    } catch (error) {
      setStatus(`Error loading firmware: ${error.message}`);
      setBarStatus('danger');
      setShowStatus(true);
    }
  };

  const parseFirmwareNotification = (value) => {
    if (value.byteLength >= 20) {
      const crc = value.getUint16(18, true);
      const crcRecv = crc16(0, new Uint8Array(value.buffer, 0, 18), 18);
      if (crcRecv === crc) {
        const fwAns = value.getUint16(2, true);
        if (fwAns === 0 && expectedIndexRef.current === value.getUint16(0, true)) {
          fwStatusRef.current = 1;
        } else if (fwAns === 1 && expectedIndexRef.current === value.getUint16(0, true)) {
          fwStatusRef.current = 0;
          setStatus('CRC Error');
        } else if (fwAns === 2) {
          fwStatusRef.current = 0;
          setStatus('Index Error');
        } else if (fwAns === 3 && expectedIndexRef.current === value.getUint16(0, true)) {
          fwStatusRef.current = 0;
          setStatus('Payload length Error');
        }
      }
    }
  };

  const parseCommandNotification = (value) => {
    if (value.byteLength >= 20) {
      const crc = value.getUint16(18, true);
      const crcRecv = crc16(0, new Uint8Array(value.buffer, 0, 18), 18);
      if (crcRecv === crc) {
        if (value.getUint16(0, true) === 3) {
          if (value.getUint16(2, true) === 1 || value.getUint16(2, true) === 2 || value.getUint16(2, true) === 4) {
            const ans = value.getUint16(4, true);
            if (ans === 0) {
              cmdStatusRef.current = 1;
            } else if (ans === 1) {
              if (value.getUint16(2, true) === 1)
                setStatus('NACK on START command');
              else if (value.getUint16(2, true) === 2)
                setStatus('NACK on STOP command');
              cmdStatusRef.current = 0;
            } else if (ans === 3) {
              setStatus('Signature Error');
              cmdStatusRef.current = 0;
            }
          }
        }
      }
    }
  };

  const scanClick = async () => {
    if (isConnected) {
      setConnection(false);
      if (clientDeviceRef.current?.device) {
        try {
          await clientDeviceRef.current.device.gatt.disconnect();
        } catch (e) {
          console.error(e);
        }
      }
    }
    try {
      setShowStatus(false);
      setConnection(false);
      setLoadFile(false);
      setProgressBar(false);
      setDISInfo(false);

      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: ['00008018-0000-1000-8000-00805f9b34fb'] }],
        optionalServices: ['0000180a-0000-1000-8000-00805f9b34fb', '12345678-1234-1234-1234-123456789012']
      });

      const server = await device.gatt.connect();
      setDevice(device.name);
      clientDeviceRef.current = { device, server };
      addLog(`Connected to ${device.name}`);

      const service = await server.getPrimaryService('00008018-0000-1000-8000-00805f9b34fb');
      
      const fwChar = await service.getCharacteristic('00008020-0000-1000-8000-00805f9b34fb');
      await fwChar.startNotifications();
      fwChar.addEventListener('characteristicvaluechanged', (e) => parseFirmwareNotification(e.target.value));
      
      const cmdChar = await service.getCharacteristic('00008022-0000-1000-8000-00805f9b34fb');
      await cmdChar.startNotifications();
      cmdChar.addEventListener('characteristicvaluechanged', (e) => parseCommandNotification(e.target.value));

      // Read device info
      try {
        const disService = await server.getPrimaryService('0000180a-0000-1000-8000-00805f9b34fb');
        
        try {
          const modelChar = await disService.getCharacteristic('00002a24-0000-1000-8000-00805f9b34fb');
          const modelValue = await modelChar.readValue();
          setModel(new TextDecoder().decode(modelValue));
          setDISInfo(true);
        } catch (e) {
          setModel('');
        }
        try {
          const swVersionChar = await disService.getCharacteristic('00002a26-0000-1000-8000-00805f9b34fb');
          const swVersionValue = await swVersionChar.readValue();
          setSWVersion(new TextDecoder().decode(swVersionValue));
          setDISInfo(true);
        } catch (e) {
          setSWVersion('');
        }
        try {
          const hwVersionChar = await disService.getCharacteristic('00002a27-0000-1000-8000-00805f9b34fb');
          const hwVersionValue = await hwVersionChar.readValue();
          setHWVersion(new TextDecoder().decode(hwVersionValue));
          setDISInfo(true);
        } catch (e) {
          setHWVersion('');
        }
        try {
          const mfgChar = await disService.getCharacteristic('00002a29-0000-1000-8000-00805f9b34fb');
          const mfgValue = await mfgChar.readValue();
          setManufacturer(new TextDecoder().decode(mfgValue));
          setDISInfo(true);
        } catch (e) {
          setManufacturer('');
        }
      } catch (error) {
        console.log('Device info read error');
      }

      // Read signal settings
      try {
        const settingsService = await server.getPrimaryService('12345678-1234-1234-1234-123456789012');
        
        try {
          const nameChar = await settingsService.getCharacteristic('87654321-4321-4321-4321-210987654321');
          const nameValue = await nameChar.readValue();
          setSignalName(new TextDecoder().decode(nameValue));
        } catch (e) {
          setSignalName('');
        }
        
        try {
          const idChar = await settingsService.getCharacteristic('87654321-4321-4321-4321-210987654322');
          const idValue = await idChar.readValue();
          setSignalId(new TextDecoder().decode(idValue));
        } catch (e) {
          setSignalId('');
        }
        
        try {
          const channelChar = await settingsService.getCharacteristic('87654321-4321-4321-4321-210987654323');
          const channelValue = await channelChar.readValue();
          const channelArray = new Uint8Array(channelValue.buffer, channelValue.byteOffset, channelValue.byteLength);
          setSignalChannel(channelArray[0].toString());
        } catch (e) {
          setSignalChannel('');
        }

        try {
          const secondaryChannelChar = await settingsService.getCharacteristic('87654321-4321-4321-4321-210987654326');
          const secondaryChannelValue = await secondaryChannelChar.readValue();
          const secondaryChannelArray = new Uint8Array(secondaryChannelValue.buffer, secondaryChannelValue.byteOffset, secondaryChannelValue.byteLength);
          setSecondaryChannel(secondaryChannelArray[0].toString());
          setHasSecondaryChannelSetting(true);
        } catch (e) {
          setSecondaryChannel('');
          setHasSecondaryChannelSetting(false);
        }

        try {
          const frequencyHoppingChar = await settingsService.getCharacteristic('87654321-4321-4321-4321-210987654327');
          const frequencyHoppingValue = await frequencyHoppingChar.readValue();
          const frequencyHoppingArray = new Uint8Array(frequencyHoppingValue.buffer, frequencyHoppingValue.byteOffset, frequencyHoppingValue.byteLength);
          setFrequencyHopping(frequencyHoppingArray[0] === 1 ? 1 : 0);
          setHasFrequencyHoppingSetting(true);
        } catch (e) {
          setFrequencyHopping(0);
          setHasFrequencyHoppingSetting(false);
        }
        
        try {
          const debugChar = await settingsService.getCharacteristic('87654321-4321-4321-4321-210987654324');
          const debugValue = await debugChar.readValue();
          const debugInt = new Uint8Array(debugValue.buffer, debugValue.byteOffset, debugValue.byteLength)[0];
          setInputDebug(debugInt);
        } catch (e) {
          setInputDebug(0);
        }
        
        try {
          const blockChar = await settingsService.getCharacteristic('87654321-4321-4321-4321-210987654325');
          const blockValue = await blockChar.readValue();
          const blockInt = new Uint8Array(blockValue.buffer, blockValue.byteOffset, blockValue.byteLength)[0];
          setBlockAvailableSetting(blockInt);
        } catch (e) {
          setBlockAvailableSetting(0);
        }
      } catch (error) {
        console.log('Settings read error');
      }

      setConnection(true);
    } catch (e) {
      addLog(`Connection error: ${e.message}`);
      console.error(e);
    }
  };

  const disconnectClick = () => {
    setLoadFile(false);
    setProgressBar(false);
    if (isConnected) {
      setConnection(false);
      if (clientDeviceRef.current?.device) {
        clientDeviceRef.current.device.gatt.disconnect();
      }
    }
  };

  const waitForAnsCommand = (timeout) => {
    return new Promise((resolve) => {
      if (cmdStatusRef.current === 1) resolve(true);
      let intervalId = null;

      const checkCondition = () => {
        if (cmdStatusRef.current === 1) {
          if (intervalId !== null) clearInterval(intervalId);
          resolve(true);
        }
      };

      intervalId = setInterval(checkCondition, 50);

      setTimeout(() => {
        if (intervalId !== null) {
          clearInterval(intervalId);
          resolve(false);
        }
      }, timeout);
    });
  };

  const waitForAnsFw = (timeout) => {
    return new Promise((resolve) => {
      if (fwStatusRef.current === 1) {
        resolve(true);
      }
      let intervalId = null;

      const checkCondition = () => {
        if (fwStatusRef.current === 1) {
          if (intervalId !== null) clearInterval(intervalId);
          resolve(true);
        }
      };

      intervalId = setInterval(checkCondition, 50);

      setTimeout(() => {
        if (intervalId !== null) {
          clearInterval(intervalId);
          resolve(false);
        }
      }, timeout);
    });
  };

  const sendFirmware = async (packetSize, file) => {
    const startTime = Date.now();
    let index = 0;
    let writtenSize = 0;

    try {
      // Fetch service and characteristic once, outside the loop
      const service = await clientDeviceRef.current.server.getPrimaryService('00008018-0000-1000-8000-00805f9b34fb');
      const fwChar = await service.getCharacteristic('00008020-0000-1000-8000-00805f9b34fb');

      while (writtenSize < file.byteLength) {
        let sectorSize = 0;
        let sequence = 0;
        let crc = 0;
        let fLast = false;

        const sector = file.slice(writtenSize, writtenSize + 4096);
        if (sector.byteLength === 0) break;

        while (sectorSize < sector.byteLength) {
          let toRead = packetSize - 3;
          if (sectorSize + toRead > sector.byteLength) {
            toRead = sector.byteLength - sectorSize;
            fLast = true;
          }

          const sectorData = sector.slice(sectorSize, sectorSize + toRead);
          sectorSize += toRead;
          if (sectorSize >= 4096) fLast = true;

          crc = crc16(crc, new Uint8Array(sectorData), sectorData.byteLength);

          if (fLast) sequence = 0xff;

          const packet = new Uint8Array(toRead + 3);
          packet[0] = index & 0xff;
          packet[1] = (index >> 8) & 0xff;
          packet[2] = sequence;
          new Uint8Array(sectorData).forEach((byte, i) => {
            packet[3 + i] = byte;
          });

          writtenSize += sectorData.byteLength;

          let packetToSend;
          if (fLast) {
            const crcData = new Uint8Array(2);
            crcData[0] = crc & 0xff;
            crcData[1] = (crc >> 8) & 0xff;

            packetToSend = new Uint8Array(packet.length + 2);
            packetToSend.set(packet);
            packetToSend.set(crcData, packet.length);

            const pStatus = Math.round((100 * writtenSize) / file.byteLength);
            setProgress(pStatus);
            setUploadSpeed((writtenSize * 1000) / 1024 / (Date.now() - startTime));
          } else {
            packetToSend = packet;
          }

          fwStatusRef.current = 0;
          expectedIndexRef.current = index;

          // Use writeValueWithoutResponse for data packets (like original BLEOTA's writeWithoutResponse)
          // Fall back to writeValue if writeValueWithoutResponse is not available
          if (fwChar.writeValueWithoutResponse) {
            await fwChar.writeValueWithoutResponse(packetToSend);
          } else {
            await fwChar.writeValue(packetToSend);
          }

          if (fLast) {
            await waitForAnsFw(3000);
          }

          sequence += 1;
        }
        index += 1;
      }
    } catch (error) {
      throw error;
    }
  };

  const otaClick = async () => {
    if (otaFile == null) return;
    setLoadFile(false);

    const buffer = new Uint8Array(20);
    addLog('Start App OTA');
    buffer[0] = 0x01;
    buffer[1] = 0x00;

    const view = new DataView(buffer.buffer);
    view.setUint32(2, otaFile.byteLength, true);
    const crc = crc16(0, buffer, 18);
    view.setUint16(18, crc, true);

    cmdStatusRef.current = 0;
    try {
      const service = await clientDeviceRef.current.server.getPrimaryService('00008018-0000-1000-8000-00805f9b34fb');
      const cmdChar = await service.getCharacteristic('00008022-0000-1000-8000-00805f9b34fb');
      
      await cmdChar.writeValue(buffer);

      const cmdAck = await waitForAnsCommand(5000);
      if (!cmdAck) {
        setBarStatus('danger');
        setShowStatus(true);
        setStatus('Start command rejected');
        return;
      }

      addLog('cmd acked');
      setProgress(0);
      setProgressBar(true);
      await sendFirmware(510, otaFile);

      addLog('Stop OTA');
      buffer.fill(0);
      buffer[0] = 0x02;
      buffer[1] = 0x00;
      
      const stopCrc = crc16(0, buffer, 18);
      view.setUint16(18, stopCrc, true);

      cmdStatusRef.current = 0;
      
      try {
        await cmdChar.writeValue(buffer);

        const cmdAck2 = await waitForAnsCommand(5000);
        if (!cmdAck2) {
          addLog('Stop command rejected');
          return;
        }
      } catch (stopErr) {
        addLog('Device disconnected - OTA completed');
      }

      setConnection(false);
      setProgressBar(false);
      setLoadFile(false);
      setStatus('OTA Done');
      setBarStatus('success');
      setShowStatus(true);
      clientDeviceRef.current.device.gatt.disconnect();
    } catch (e) {
      addLog(`OTA Error: ${e.message}`);
      setBarStatus('danger');
      setShowStatus(true);
      setStatus('OTA Error');
    }
  };

  const writeSettings = async () => {
    if (!signalName.trim()) {
      setStatus('Signal name is required');
      setBarStatus('danger');
      setShowStatus(true);
      return;
    }
    if (signalId.length !== 3) {
      setStatus('Signal ID must be exactly 3 characters');
      setBarStatus('danger');
      setShowStatus(true);
      return;
    }
    const channelNum = parseInt(signalChannel, 10);
    if (isNaN(channelNum) || channelNum < 0 || channelNum > 63) {
      setStatus('Signal channel must be between 0 and 63');
      setBarStatus('danger');
      setShowStatus(true);
      return;
    }

    const secondaryChannelNum = parseInt(secondaryChannel, 10);
    if (hasSecondaryChannelSetting && (isNaN(secondaryChannelNum) || secondaryChannelNum < 0 || secondaryChannelNum > 63)) {
      setStatus('Secondary channel must be between 0 and 63');
      setBarStatus('danger');
      setShowStatus(true);
      return;
    }

    try {
      const service = await clientDeviceRef.current.server.getPrimaryService('12345678-1234-1234-1234-123456789012');
      
      const nameChar = await service.getCharacteristic('87654321-4321-4321-4321-210987654321');
      const nameData = new TextEncoder().encode(signalName);
      await nameChar.writeValue(nameData);

      const idChar = await service.getCharacteristic('87654321-4321-4321-4321-210987654322');
      const idData = new TextEncoder().encode(signalId);
      await idChar.writeValue(idData);

      const channelChar = await service.getCharacteristic('87654321-4321-4321-4321-210987654323');
      const channelData = new TextEncoder().encode(channelNum);
      await channelChar.writeValue(channelData);

      if (hasSecondaryChannelSetting) {
        const secondaryChannelChar = await service.getCharacteristic('87654321-4321-4321-4321-210987654326');
        const secondaryChannelData = new TextEncoder().encode(secondaryChannelNum);
        await secondaryChannelChar.writeValue(secondaryChannelData);
      }

      if (hasFrequencyHoppingSetting) {
        const frequencyHoppingChar = await service.getCharacteristic('87654321-4321-4321-4321-210987654327');
        const frequencyHoppingData = new TextEncoder().encode(frequencyHopping);
        await frequencyHoppingChar.writeValue(frequencyHoppingData);
      }

      const debugChar = await service.getCharacteristic('87654321-4321-4321-4321-210987654324');
      const debugData = new TextEncoder().encode(inputDebug);
      await debugChar.writeValue(debugData);

      const blockChar = await service.getCharacteristic('87654321-4321-4321-4321-210987654325');
      const blockData = new TextEncoder().encode(blockAvailableSetting);
      await blockChar.writeValue(blockData);

      setStatus('Settings saved successfully');
      setBarStatus('success');
      setShowStatus(true);
    } catch (error) {
      setStatus(`Error saving settings: ${error.message}`);
      setBarStatus('danger');
      setShowStatus(true);
    }
  };

  return (
    <div className="firmware-page">
      <Navigation />
      <div className="header-bar">
        <h1>Signals Firmware Update</h1>
        {!isConnected && (
          <button className="btn-scan" onClick={scanClick}>
            Scan
          </button>
        )}
        {isConnected && (
          <button className="btn-disconnect" onClick={disconnectClick}>
            Disconnect
          </button>
        )}
      </div>

      {showStatus && (
        <div className={`status-card status-${barStatus}`}>
          <div className="status-text">{status}</div>
        </div>
      )}

      {isConnected && (
        <div className="content">
          <h2>{device}</h2>

          {disInfo && (
            <div className="device-info-card">
              <h3>Device Information</h3>
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Manufacturer:</span>
                  <span className="info-value">{manufacturer}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Model:</span>
                  <span className="info-value">{model}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">HW Version:</span>
                  <span className="info-value">{hwVersion}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">FW Version:</span>
                  <span className="info-value">{swVersion}</span>
                </div>
              </div>
            </div>
          )}

          <div className="settings-card">
            <h3>Signal Settings</h3>
            <div className="settings-form">
              <div className="form-group">
                <label htmlFor="signal-name">Signal Name</label>
                <input
                  id="signal-name"
                  type="text"
                  placeholder="Enter signal name"
                  value={signalName}
                  onChange={(e) => setSignalName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="signal-id">Signal ID (3 chars)</label>
                <input
                  id="signal-id"
                  type="text"
                  placeholder="e.g., ABC"
                  maxLength="3"
                  value={signalId}
                  onChange={(e) => setSignalId(e.target.value.toUpperCase())}
                />
              </div>
              <div className="form-group">
                <label htmlFor="signal-channel">Signal Channel (0-63)</label>
                <input
                  id="signal-channel"
                  type="number"
                  placeholder="0-63"
                  min="0"
                  max="63"
                  value={signalChannel}
                  onChange={(e) => setSignalChannel(e.target.value)}
                />
              </div>
              {hasSecondaryChannelSetting && (
                <div className="form-group">
                  <label htmlFor="secondary-channel">Secondary Channel (0-63)</label>
                  <input
                    id="secondary-channel"
                    type="number"
                    placeholder="0-63"
                    min="0"
                    max="63"
                    value={secondaryChannel}
                    onChange={(e) => setSecondaryChannel(e.target.value)}
                  />
                </div>
              )}
              {hasFrequencyHoppingSetting && (
                <div className="form-group">
                  <div className="checkbox-group">
                    <input
                      id="frequency-hopping"
                      type="checkbox"
                      checked={frequencyHopping === 1}
                      onChange={(e) => setFrequencyHopping(e.target.checked ? 1 : 0)}
                    />
                    <label htmlFor="frequency-hopping">Enable Frequency Hopping</label>
                  </div>
                  <p className="form-help-text">When enabled, the device will use the frequency hopping mode that is available on newer firmware revisions.</p>
                </div>
              )}
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    id="input-debug"
                    type="checkbox"
                    checked={inputDebug === 1}
                    onChange={(e) => setInputDebug(e.target.checked ? 1 : 0)}
                  />
                  <label htmlFor="input-debug">Enable Input Debug LED</label>
                </div>
                <p className="form-help-text">This determines whether the white LED will turn on to indicate that an input received a signal.  While helpful for debugging, turning this off when the system is in normal operation reduces power consumption by 50% and could double the life of a signal operating off a battery.</p>
              </div>
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    id="block-available"
                    type="checkbox"
                    checked={blockAvailableSetting === 1}
                    onChange={(e) => setBlockAvailableSetting(e.target.checked ? 1 : 0)}
                  />
                  <label htmlFor="block-available">Enable Block Available Indicator</label>
                </div>
                <p className="form-help-text">When enabled, the signal will display green when the block is available. When disabled, the signal will remain dark when the block is available.</p>
              </div>
              <button className="btn-save-settings" onClick={writeSettings}>
                Save Settings
              </button>
            </div>
          </div>

          <div className="ota-control-card">
            <div className="firmware-selector-group">
              <label htmlFor="firmware-select">Firmware Revision:</label>
              <select
                id="firmware-select"
                value={selectedFirmware}
                onChange={(e) => {
                  setSelectedFirmware(e.target.value);
                  loadSelectedFirmware(e.target.value);
                }}
              >
                <option value="">-- Select Firmware --</option>
                {firmwareFiles.map((file) => (
                  <option key={file} value={file}>
                    {file}
                  </option>
                ))}
              </select>
            </div>

            {showBar && (
              <div className="speed-display">
                {uploadSpeed.toFixed(2)} kB/s
              </div>
            )}

            {showBar && (
              <div className="progress-group">
                <div className="progress-bar-container">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${progress}%` }}
                  ></div>
                  <div className="progress-percent">{progress}%</div>
                </div>
              </div>
            )}

            <div className="button-group">
              {isFileLoaded && (
                <button className="btn-start" onClick={otaClick}>
                  Start
                </button>
              )}
            </div>
          </div>

          <div className="log-output">
            {logMessages.length === 0 ? (
              <div>Ready to connect...</div>
            ) : (
              logMessages.map((msg, idx) => (
                <div key={idx}>{msg}</div>
              ))
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default SignalsFirmware;
